import React, { useState } from 'react'
import { apiPost } from '../../utils/api'

interface RatingWidgetProps {
  chatId: string
  existingRating?: { rating: number; feedback_text?: string }
}

export const RatingWidget: React.FC<RatingWidgetProps> = ({ chatId, existingRating }) => {
  const [rating, setRating] = useState(existingRating?.rating || 0)
  const [feedback, setFeedback] = useState(existingRating?.feedback_text || '')
  const [submitted, setSubmitted] = useState(!!existingRating)
  const [hovering, setHovering] = useState(0)

  const handleSubmit = async () => {
    if (rating === 0) return
    try {
      await apiPost(`/chats/${chatId}/rating`, { rating, feedback_text: feedback || undefined })
      setSubmitted(true)
    } catch (err) {
      console.error('Failed to submit rating:', err)
    }
  }

  if (submitted) {
    return (
      <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 border-t border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium text-green-700 dark:text-green-300">
            Rated {rating}/5 {feedback && `- "${feedback}"`}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-3 bg-surface border-t border-border">
      <p className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-2">Customer Satisfaction</p>
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onMouseEnter={() => setHovering(star)}
            onMouseLeave={() => setHovering(0)}
            onClick={() => setRating(star)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <svg
              className={`w-6 h-6 ${(hovering || rating) >= star ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
            </svg>
          </button>
        ))}
      </div>
      {rating > 0 && (
        <div className="space-y-2">
          <input
            type="text"
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Optional feedback..."
            className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border text-sm outline-none focus:border-brand-500 transition-colors"
          />
          <button
            onClick={handleSubmit}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          >
            Submit Rating
          </button>
        </div>
      )}
    </div>
  )
}
